import { useState } from "react";
import { KeyRound, ListPlus, Server, ShieldCheck } from "lucide-react";
import type { CustomEmailProvider } from "../types";
import { api } from "../lib/api";
import {
  blankAccount,
  defaultDisplayName,
  providerPreset,
  type AccountForm
} from "../lib/accountPresets";
import { Button, Modal, Switch } from "./ui";

type BulkDraft = {
  localPart: string;
  displayName: string;
  secret: string;
};

type Notify = (tone: "success" | "error" | "info", title: string, message?: string) => void;

export default function BulkAccountsModal({
  open,
  providers,
  onClose,
  onCreated,
  notify
}: {
  open: boolean;
  providers: CustomEmailProvider[];
  onClose: () => void;
  onCreated: () => void | Promise<void>;
  notify: Notify;
}) {
  const [domain, setDomain] = useState("");
  const [localPartsText, setLocalPartsText] = useState("");
  const [entries, setEntries] = useState<BulkDraft[]>([]);
  const [passwordMode, setPasswordMode] = useState<"Shared" | "Individual">("Shared");
  const [sharedSecret, setSharedSecret] = useState("");
  const [settings, setSettings] = useState<AccountForm>(blankAccount);
  const [saving, setSaving] = useState(false);

  const close = () => {
    setDomain("");
    setLocalPartsText("");
    setEntries([]);
    setSharedSecret("");
    setPasswordMode("Shared");
    setSettings(blankAccount);
    onClose();
  };

  const selectProvider = (providerType: string) => {
    setSettings((current) => providerPreset(providerType, current, providers));
  };

  const prepareAccounts = () => {
    const values = localPartsText
      .split(/[\s,;]+/)
      .map((value) => value.trim())
      .filter(Boolean);
    if (values.length === 0) {
      notify("error", "No local parts entered", "Enter usernames such as reservations, info, and billing.");
      return;
    }
    const invalid = values.find((value) => value.includes("@"));
    if (invalid) {
      notify("error", "Use local parts only", `Enter '${invalid.split("@")[0]}' instead of a full email address.`);
      return;
    }
    const unique = [...new Map(values.map((value) => [value.toLowerCase(), value])).values()];
    const existing = new Map(entries.map((entry) => [entry.localPart.toLowerCase(), entry]));
    setEntries(unique.map((localPart) => existing.get(localPart.toLowerCase()) ?? {
      localPart,
      displayName: defaultDisplayName(localPart),
      secret: ""
    }));
    if (unique.length !== values.length)
      notify("info", "Duplicate local parts removed", `${unique.length} unique accounts are ready for review.`);
  };

  const updateEntry = (index: number, patch: Partial<BulkDraft>) => {
    setEntries((current) => current.map((entry, itemIndex) =>
      itemIndex === index ? { ...entry, ...patch } : entry));
  };

  const submit = async () => {
    if (entries.length === 0) {
      notify("error", "Prepare the account list", "Review the local parts before adding the accounts.");
      return;
    }
    setSaving(true);
    try {
      const result = await api.createAccountsBulk({
        domain,
        providerType: settings.providerType,
        authType: settings.authType,
        incomingProtocol: settings.incomingProtocol,
        incomingServer: settings.incomingServer,
        incomingPort: settings.incomingPort,
        incomingUseSsl: settings.incomingUseSsl,
        outgoingServer: settings.outgoingServer,
        outgoingPort: settings.outgoingPort,
        outgoingUseSsl: settings.outgoingUseSsl,
        passwordMode,
        sharedSecret: passwordMode === "Shared" ? sharedSecret : null,
        isActive: settings.isActive,
        accounts: entries.map((entry) => ({
          localPart: entry.localPart,
          displayName: entry.displayName,
          secret: passwordMode === "Individual" ? entry.secret : null
        }))
      });
      notify("success", "Email accounts added", `${result.createdCount} accounts were added securely.`);
      await onCreated();
      close();
    } catch (error) {
      notify("error", "Couldn’t add accounts", (error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Add email accounts in bulk"
      subtitle="Reuse one domain and server setup, then choose a shared or individual password for each mailbox."
      wide
    >
      <div className="bulk-account-editor">
        <section className="form-section">
          <div className="form-section-heading">
            <span>1</span>
            <div>
              <h3>Domain and local parts</h3>
              <p>Enter only the portion before @. Separate entries with a new line, comma, or space.</p>
            </div>
          </div>
          <div className="bulk-source-grid">
            <label className="field">
              <span>Email domain</span>
              <div className="domain-input">
                <span>@</span>
                <input
                  value={domain}
                  onChange={(event) => setDomain(event.target.value.replace(/^@/, ""))}
                  placeholder="company.com"
                  autoComplete="off"
                />
              </div>
            </label>
            <label className="field bulk-local-parts">
              <span>Email local parts / usernames</span>
              <textarea
                value={localPartsText}
                onChange={(event) => {
                  setLocalPartsText(event.target.value);
                  setEntries([]);
                }}
                placeholder={"reservations\ninfo\nbilling"}
                rows={4}
              />
            </label>
            <Button tone="secondary" icon={<ListPlus size={16} />} onClick={prepareAccounts}>
              Prepare list
            </Button>
          </div>
        </section>

        <section className="form-section">
          <div className="form-section-heading">
            <span>2</span>
            <div>
              <h3>Provider and mail servers</h3>
              <p>Select a saved provider or enter the IMAP/SMTP setup once for every account.</p>
            </div>
          </div>
          <div className="form-grid two bulk-provider-row">
            <label className="field">
              <span>Provider</span>
              <select value={settings.providerType} onChange={(event) => selectProvider(event.target.value)}>
                <option value="Custom">Custom / enter manually</option>
                <option value="Gmail">Gmail</option>
                <option value="Yahoo">Yahoo</option>
                {providers.map((provider) => (
                  <option value={provider.name} key={provider.id}>{provider.name}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Authentication</span>
              <select
                value={settings.authType}
                onChange={(event) => setSettings({ ...settings, authType: event.target.value })}
              >
                <option value="Password">Password</option>
                <option value="AppPassword">App password</option>
              </select>
            </label>
          </div>
          <div className="form-grid server-grid bulk-server-grid">
            <label className="field">
              <span>Incoming protocol</span>
              <select
                value={settings.incomingProtocol}
                onChange={(event) => setSettings({ ...settings, incomingProtocol: event.target.value })}
              >
                <option>IMAP</option>
                <option>POP</option>
              </select>
            </label>
            <label className="field server-host">
              <span>Incoming server</span>
              <input
                value={settings.incomingServer}
                onChange={(event) => setSettings({ ...settings, incomingServer: event.target.value })}
                placeholder="imap.company.com"
              />
            </label>
            <label className="field">
              <span>Port</span>
              <input
                type="number"
                value={settings.incomingPort}
                onChange={(event) => setSettings({ ...settings, incomingPort: Number(event.target.value) })}
              />
            </label>
            <label className="field server-host">
              <span>Outgoing SMTP server</span>
              <input
                value={settings.outgoingServer}
                onChange={(event) => setSettings({ ...settings, outgoingServer: event.target.value })}
                placeholder="smtp.company.com"
              />
            </label>
            <label className="field">
              <span>Port</span>
              <input
                type="number"
                value={settings.outgoingPort}
                onChange={(event) => setSettings({ ...settings, outgoingPort: Number(event.target.value) })}
              />
            </label>
          </div>
          <div className="inline-switches">
            <label>
              <Switch
                checked={settings.incomingUseSsl}
                onChange={(incomingUseSsl) => setSettings({ ...settings, incomingUseSsl })}
                label="Incoming SSL"
              />
              Incoming SSL/TLS
            </label>
            <label>
              <Switch
                checked={settings.outgoingUseSsl}
                onChange={(outgoingUseSsl) => setSettings({ ...settings, outgoingUseSsl })}
                label="Outgoing SSL"
              />
              Outgoing SSL/TLS
            </label>
          </div>
        </section>

        <section className="form-section">
          <div className="form-section-heading">
            <span>3</span>
            <div>
              <h3>Passwords and display names</h3>
              <p>Each password is encrypted separately on the server, including shared passwords.</p>
            </div>
          </div>
          <div className="password-mode" role="group" aria-label="Password assignment mode">
            <button
              type="button"
              className={passwordMode === "Shared" ? "active" : ""}
              onClick={() => setPasswordMode("Shared")}
            >
              <ShieldCheck size={16} />
              <span><strong>One shared password</strong><small>Apply one password to every new account</small></span>
            </button>
            <button
              type="button"
              className={passwordMode === "Individual" ? "active" : ""}
              onClick={() => setPasswordMode("Individual")}
            >
              <KeyRound size={16} />
              <span><strong>Individual passwords</strong><small>Enter a different password per account</small></span>
            </button>
          </div>
          {passwordMode === "Shared" && (
            <label className="field shared-password-field">
              <span>Shared password</span>
              <input
                type="password"
                value={sharedSecret}
                onChange={(event) => setSharedSecret(event.target.value)}
                autoComplete="new-password"
                placeholder="Applied securely to all accounts"
              />
            </label>
          )}
          <div className="bulk-account-list">
            <header className={passwordMode === "Individual" ? "with-password" : ""}>
              <span>Email address</span>
              <span>Display name</span>
              {passwordMode === "Individual" && <span>Password</span>}
            </header>
            {entries.length === 0 ? (
              <div className="bulk-account-placeholder">
                <Server size={20} />
                <span>Prepare the local-part list to review names and passwords here.</span>
              </div>
            ) : entries.map((entry, index) => (
              <div className={`bulk-account-row ${passwordMode === "Individual" ? "with-password" : ""}`} key={entry.localPart.toLowerCase()}>
                <span className="bulk-address">{entry.localPart}@{domain || "company.com"}</span>
                <input
                  aria-label={`Display name for ${entry.localPart}`}
                  value={entry.displayName}
                  onChange={(event) => updateEntry(index, { displayName: event.target.value })}
                />
                {passwordMode === "Individual" && (
                  <input
                    aria-label={`Password for ${entry.localPart}`}
                    type="password"
                    value={entry.secret}
                    onChange={(event) => updateEntry(index, { secret: event.target.value })}
                    autoComplete="new-password"
                    placeholder="Required"
                  />
                )}
              </div>
            ))}
          </div>
          <div className="switch-row bulk-active-switch">
            <div>
              <strong>Accounts active immediately</strong>
              <span>You can test each connection after the accounts are added.</span>
            </div>
            <Switch
              checked={settings.isActive}
              onChange={(isActive) => setSettings({ ...settings, isActive })}
              label="Accounts active"
            />
          </div>
        </section>
      </div>
      <div className="modal-footer">
        <span className="modal-footer-summary">{entries.length} account{entries.length === 1 ? "" : "s"} ready</span>
        <Button tone="secondary" onClick={close}>Cancel</Button>
        <Button onClick={submit} disabled={saving || entries.length === 0}>
          {saving ? "Adding securely…" : `Add ${entries.length || ""} account${entries.length === 1 ? "" : "s"}`}
        </Button>
      </div>
    </Modal>
  );
}